/**
 * Image protection — casual-friction deterrents.
 *
 * IMPORTANT: this cannot prevent a determined visitor from saving an image.
 * Everything the browser renders is already on the visitor's disk, and the raw
 * CDN URLs stay reachable. This only raises the bar for casual right-click /
 * drag / long-press saving, plus an opt-in DevTools deterrent.
 *
 * Development escape hatches (see isBypassed):
 *   - Shopify theme editor (Shopify.designMode)
 *   - localhost / 127.0.0.1 / tunnel hostnames (shopify theme dev)
 *   - ?image_protection=off  (sticky for the tab, via sessionStorage)
 *   - window.imageProtection.disable() from the console
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'bricks:image-protection:off';
  const ROOT_CLASS = 'images-protected';
  const DEV_HOST_SUFFIXES = ['.local', '.ngrok.io', '.ngrok-free.app', '.trycloudflare.com'];
  const DEV_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

  function readConfig() {
    const defaults = {
      scope: 'all',
      blockDevtoolsShortcuts: false,
      detectDevtools: false,
      antiDebug: false,
      blurOnDetect: true,
    };

    const el = document.getElementById('image-protection-config');
    if (!el) return defaults;

    try {
      return Object.assign(defaults, JSON.parse(el.textContent));
    } catch (error) {
      return defaults;
    }
  }

  const config = readConfig();

  function sessionFlag(value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(STORAGE_KEY) === '1';
      if (value) window.sessionStorage.setItem(STORAGE_KEY, '1');
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* storage blocked (private mode, cookie prefs) — fall through */
    }
    return !!value;
  }

  function isDevHost() {
    const host = window.location.hostname;
    if (DEV_HOSTS.indexOf(host) !== -1) return true;
    return DEV_HOST_SUFFIXES.some(function (suffix) {
      return host.endsWith(suffix);
    });
  }

  function isBypassed() {
    // Theme editor: blocked context menus and key handlers make it unusable.
    if (window.Shopify && window.Shopify.designMode) return true;
    if (isDevHost()) return true;

    try {
      const param = new URL(window.location.href).searchParams.get('image_protection');
      if (param === 'off') return sessionFlag(true);
      if (param === 'on') {
        sessionFlag(false);
        return false;
      }
    } catch (error) {
      /* URL unavailable — fall through to the stored flag */
    }

    return sessionFlag();
  }

  /* ------------------------------------------------------------------ */
  /* Scope                                                               */
  /* ------------------------------------------------------------------ */

  const PRODUCT_SELECTORS = [
    '.product-media-container',
    '.product-media',
    '.card-gallery',
    '.dialog-zoomed-gallery',
    'media-gallery',
    'product-media-gallery',
    '[data-protect-images]',
  ].join(',');

  function isProtectedTarget(target) {
    if (!target || target.nodeType !== 1) return false;

    const media = target.closest('img, picture, [data-protect-images]');
    if (!media) return false;
    if (media.closest('[data-protect-images="false"]')) return false;
    if (config.scope === 'all') return true;

    return !!media.closest(PRODUCT_SELECTORS);
  }

  /* ------------------------------------------------------------------ */
  /* Layer 1 — pointer / touch friction                                  */
  /* ------------------------------------------------------------------ */

  function onContextMenu(event) {
    // Scoped to media only: a document-wide block breaks copy/paste,
    // spellcheck and "open link in new tab" for legitimate visitors.
    if (isProtectedTarget(event.target)) event.preventDefault();
  }

  function onDragStart(event) {
    if (isProtectedTarget(event.target)) event.preventDefault();
  }

  /* ------------------------------------------------------------------ */
  /* DevTools deterrent (best effort, trivially bypassed)                */
  /* ------------------------------------------------------------------ */

  const SHORTCUT_KEYS = { i: true, j: true, c: true, u: true, s: true };

  function onKeyDown(event) {
    const key = (event.key || '').toLowerCase();

    if (key === 'f12') {
      event.preventDefault();
      return;
    }

    if (!SHORTCUT_KEYS[key]) return;
    if (!event.ctrlKey && !event.metaKey) return;

    // Ctrl/Cmd+Shift+I/J/C and Cmd+Opt+I/J/C — DevTools panels.
    if ((event.shiftKey || event.altKey) && key !== 'u' && key !== 's') {
      event.preventDefault();
      return;
    }

    // Ctrl/Cmd+U (view source) and Ctrl/Cmd+S (save page).
    if (!event.shiftKey && !event.altKey && (key === 'u' || key === 's')) {
      // Leave Cmd+S alone inside fields — it is a legitimate keystroke there.
      const tag = (event.target && event.target.tagName) || '';
      if (key === 's' && (tag === 'INPUT' || tag === 'TEXTAREA')) return;
      event.preventDefault();
    }
  }

  let devtoolsOpen = false;

  function setDevtoolsState(open) {
    if (open === devtoolsOpen) return;
    devtoolsOpen = open;
    document.documentElement.classList.toggle('devtools-detected', open && config.blurOnDetect);
    document.dispatchEvent(new CustomEvent('image-protection:devtools', { detail: { open: open } }));
  }

  function pollDevtools() {
    // Size heuristic: docked DevTools shrink the viewport relative to the
    // window. Undocked/detached DevTools windows are not detected at all.
    const threshold = 160;
    const widthGap = window.outerWidth - window.innerWidth;
    const heightGap = window.outerHeight - window.innerHeight;
    setDevtoolsState(widthGap > threshold || heightGap > threshold);
  }

  function startAntiDebug() {
    // A `debugger` statement costs nothing when DevTools is closed and stalls
    // hard when it is open. Hostile, and disabled by "Never pause here", so it
    // stays opt-in.
    timers.push(
      window.setInterval(function () {
        const start = performance.now();
        // eslint-disable-next-line no-debugger
        debugger;
        if (performance.now() - start > 100) setDevtoolsState(true);
      }, 1000)
    );
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  let active = false;
  let timers = [];

  function enable() {
    if (active) return;
    active = true;

    document.documentElement.classList.add(ROOT_CLASS);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);

    if (config.blockDevtoolsShortcuts) document.addEventListener('keydown', onKeyDown);

    if (config.detectDevtools) {
      pollDevtools();
      timers.push(window.setInterval(pollDevtools, 1000));
      window.addEventListener('resize', pollDevtools);
    }

    if (config.antiDebug) startAntiDebug();
  }

  function disable() {
    active = false;

    document.documentElement.classList.remove(ROOT_CLASS, 'devtools-detected');
    document.removeEventListener('contextmenu', onContextMenu);
    document.removeEventListener('dragstart', onDragStart);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', pollDevtools);

    timers.forEach(window.clearInterval);
    timers = [];
  }

  window.imageProtection = {
    config: config,
    enable: function () {
      sessionFlag(false);
      enable();
    },
    disable: function () {
      sessionFlag(true);
      disable();
    },
    get active() {
      return active;
    },
    get bypassed() {
      return isBypassed();
    },
  };

  if (!isBypassed()) enable();
})();
